import posthog from 'posthog-js';

import { SubLevel, UserDetails } from '../../types';
import { isBloomberg } from '../bloomberg';
import {
  getHasLoggedIn,
  getSubLevelFromMembership,
  getSubscriptionLevel,
  hasActiveFreeTrial,
  isAdmin,
} from './auth';
import { getEnv } from './common';
import { getStreamApiBaseURL } from './fetch';

const POSTHOG_TOKEN = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN as
  | string
  | undefined;
const HOST = import.meta.env.VITE_POSTHOG_HOST as string | undefined;

// Set VITE_POSTHOG_DEV=1 to test locally
const POSTHOG_DEV = import.meta.env.VITE_POSTHOG_DEV === '1';

export const isPostHogEnabled = () => getEnv() === 'production' || POSTHOG_DEV;

let initialized = false;
export interface SampleDecision {
  on: boolean;
  pct: number;
}

export const parseSampleDecision = (body: unknown): SampleDecision | null => {
  const { on, pct } = (body ?? {}) as Partial<SampleDecision>;
  return typeof on === 'boolean' && typeof pct === 'number'
    ? { on, pct }
    : null;
};

// Plain fetch rather than fetchAPI intentionally
const sampleDecision = async (): Promise<SampleDecision | null> => {
  try {
    const rsp = await fetch(`${getStreamApiBaseURL()}/v1/posthog_sample`);
    return rsp.ok ? parseSampleDecision(await rsp.json()) : null;
  } catch (err) {
    console.error('PostHog sample error:', err);
    return null;
  }
};

const start = async () => {
  if (!isPostHogEnabled()) {
    return false;
  }
  const decision = await sampleDecision();
  if (decision?.on !== false) {
    initPostHog(undefined, decision?.pct);
  }
  return initialized;
};

let ready: Promise<boolean> = Promise.resolve(false);

// Called once, from index.tsx before React renders
export const startPostHog = () => (ready = start());

// Resolves true once PostHog is running, false if sampled out or disabled
export const postHogReady = () => ready;

// Per page load, how many times each distinct exception message has been seen.
const exceptionCounts = new Map<string, number>();

// Throttle exceptions so that the first few and every 500th are sent.
const EXCEPTION_KEEP_FIRST = 3;
const EXCEPTION_SAMPLE_EVERY = 500;

export const throttleException = (
  message: string,
  counts = exceptionCounts,
): number | null => {
  const seen = (counts.get(message) ?? 0) + 1;
  counts.set(message, seen);
  return seen <= EXCEPTION_KEEP_FIRST || seen % EXCEPTION_SAMPLE_EVERY === 0
    ? seen
    : null;
};

// posthog-js puts only $exception_list (and $exception_level) on a browser
// $exception, so the throttle key has to come from its first entry.
export const exceptionKey = (properties?: {
  $exception_list?: { type?: string; value?: string }[];
}): string => {
  const first = properties?.$exception_list?.[0];
  return `${first?.type ?? 'Error'}: ${first?.value ?? 'unknown'}`;
};

export type AuthStatus = 'authenticated' | 'preview' | 'logged_out';

// A visitor who has never logged in is in the free-preview experience; one who
// has is either logged out or browsing an Open House.
export const anonAuthStatus = (): AuthStatus =>
  getHasLoggedIn() ? 'logged_out' : 'preview';

// Percentage of browsers sampled in when this one was, kept on every event so
// counts can be scaled back up: count / (samplePct / 100)
let samplePct = 100;

const anonSuperProperties = () => ({
  environment: getEnv() || import.meta.env.MODE,
  authStatus: anonAuthStatus(),
  samplePct,
});

// No token means the visitor is definitively logged out, so the pageview can
// fire now. A token means identify is still in flight, and under
// `identified_only` a pageview sent ahead of it can never be attributed.
export const shouldCapturePageview = (
  identified: boolean,
  token: string | null,
) => identified || token == null;

export const initPostHog = (token = POSTHOG_TOKEN, pct = 100) => {
  if (initialized || !isPostHogEnabled() || token == null || token === '') {
    return;
  }
  try {
    // Starts PostHog autocapture and replay
    posthog.init(token, {
      api_host: HOST,
      defaults: '2026-05-30',

      // No allowlist, so anything with cursor:pointer is captured. Chart
      // surfaces are excluded instead, since they are the noisy part.
      // MuiSvgIcon is spared so icon buttons still register
      autocapture: {
        dom_event_allowlist: ['click'],
        css_selector_ignorelist: [
          'canvas',
          'svg:not(.MuiSvgIcon-root)',
          // In-app nav, already recorded by the $pageview that follows
          '.navigation-link',
          // Every slider drag registers, without saying which or what value
          '.MuiSlider-root',
          // Focusing a field; the option picked is still captured
          '.MuiInputBase-root',
          // posthog's own defaults, dropped if we don't repeat them
          '.ph-no-autocapture',
          '[data-ph-no-autocapture]',
        ],
      },
      capture_pageview: false,
      capture_pageleave: true,
      person_profiles: 'identified_only',

      capture_exceptions: true,

      before_send: (event) => {
        if (event == null || event.event !== '$exception') {
          return event;
        }
        const seen = throttleException(exceptionKey(event.properties));
        if (seen == null) {
          return null;
        }
        return {
          ...event,
          properties: { ...event.properties, $exception_seen_count: seen },
        };
      },
      // Web vitals were ~8% of event volume and unused
      capture_performance: { web_vitals: false },

      // Readable request bodies in the network tab. Testing only, never prod
      disable_compression: POSTHOG_DEV && getEnv() !== 'production',

      // Replay is on
      session_recording: {
        maskAllInputs: true,
        captureCanvas: { recordCanvas: false },
      },
    });

    samplePct = pct;
    posthog.register(anonSuperProperties());
    initialized = true;
  } catch (err) {
    console.error('PostHog init error:', err);
  }
};

const roleFor = (userDetails: UserDetails) => {
  if (isAdmin(userDetails)) {
    return 'Admin';
  }
  return hasActiveFreeTrial(userDetails) ? 'Trial' : 'Subscriber';
};

interface WpMembership {
  title?: string;
  period_type?: string;
}

const highestMembership = (
  userDetails: UserDetails,
): WpMembership | undefined => {
  const subLevel = getSubscriptionLevel(userDetails);
  const memberships = (userDetails.wordpress?.active_memberships ??
    []) as WpMembership[];
  return memberships.find(
    (m) => (getSubLevelFromMembership(m) as number) === subLevel,
  );
};

export const posthogSuperProperties = (userDetails: UserDetails) => ({
  planTier: SubLevel[getSubscriptionLevel(userDetails)],
  role: roleFor(userDetails),
  userType: isBloomberg() ? 'bbg' : 'web',
  isInstitutional: userDetails.isInstitutional ?? false,
  authStatus: 'authenticated' as AuthStatus,
});

// Mirrors the Appcues identify payload
export const posthogPersonProperties = (userDetails: UserDetails) => {
  const membership = highestMembership(userDetails);
  return {
    email: userDetails.email,
    firstName: userDetails.firstName,
    lastName: userDetails.lastName,
    username: userDetails.username,
    wpId: userDetails.id,
    createdAt: userDetails.wordpress?.registered_at,
    membershipTitle: membership?.title,
    membershipPeriod: membership?.period_type,
    ...posthogSuperProperties(userDetails),
  };
};

export const identifyPostHog = (userDetails: UserDetails | undefined) => {
  if (!initialized || userDetails == null) {
    return false;
  }
  try {
    posthog.identify(userDetails.sgId, posthogPersonProperties(userDetails));
    posthog.register(posthogSuperProperties(userDetails));
    return true;
  } catch (err) {
    console.error('PostHog identify error:', err);
    return false;
  }
};

export const capturePostHog = (
  event: string,
  properties: Record<string, unknown> = {},
) => {
  if (!initialized) {
    return;
  }
  try {
    posthog.capture(event, properties);
  } catch (err) {
    console.error('PostHog capture error:', err);
  }
};

// Reports an error we caught ourselves. It lands in Error Tracking next to the
// uncaught ones, with mechanism.handled = true to tell them apart, and is
// throttled by before_send like any other $exception.
export const captureExceptionPostHog = (
  error: unknown,
  properties: Record<string, unknown> = {},
) => {
  if (!initialized) {
    return;
  }
  try {
    posthog.captureException(error, properties);
  } catch (err) {
    console.error('PostHog captureException error:', err);
  }
};

export const capturePostHogPageview = () => capturePostHog('$pageview');

export const resetPostHog = () => {
  if (!initialized) {
    return;
  }
  try {
    posthog.reset();
    // reset() drops the super properties along with the distinct id, so the
    // environment and auth labels have to go back on
    posthog.register(anonSuperProperties());
  } catch (err) {
    console.error('PostHog reset error:', err);
  }
};
